import z from 'zod/v4'

export const fsnClientSearchClientSchema = z.object({
  id: z.number().int().positive(),
  name: z.string(),
  active: z.boolean(),
  phone: z.string().nullable(),
  email: z.string().nullable(),
  city: z.string().nullable(),
  state: z.string().nullable(),
  postal_code: z.string().nullable(),
  country: z.string().nullable(),
})

export const fsnClientSearchResponseSchema = z.object({
  clients: z.array(fsnClientSearchClientSchema),
})

export const fsnUnavailableResponseSchema = z.object({
  message: z.string(),
})

export const fsnImportSectionSchema = z.enum([
  'products',
  'location',
  'delivery_areas',
  'customers',
  'reviews',
  'add_ons',
  'notification_contacts',
  'social_links',
  'logo',
])

const allFsnImportSections = fsnImportSectionSchema.options

export const fsnImportWebsiteNavigationSchema = z.enum(['auto', 'skip'])

export const fsnImportProductOptionsSchema = z.object({
  fsn_product_scope: z.enum(['current_pages', 'all_active', 'none']).default('current_pages'),
  custom_product_scope: z.enum(['current_pages', 'all', 'none']).default('all'),
  create_current_page_collections: z.boolean().default(true),
  add_searchable_details: z.boolean().default(true),
})

const fsnNotificationContactPreviewRowSchema = z.object({
  channel: z.enum(['email', 'phone', 'text', 'fax']),
  value: z.string(),
  label: z.string(),
  enabled: z.boolean().default(true),
})

const fsnSocialLinkPreviewRowSchema = z.object({
  platform: z.string(),
  url: z.string(),
})

const fsnGenericPreviewRowSchema = z.record(z.string(), z.unknown())

const fsnDeliveryAreaPreviewSchema = z.object({
  preview_rows: z.array(fsnGenericPreviewRowSchema).default([]),
  distance_preview_rows: z.array(fsnGenericPreviewRowSchema).default([]),
  distance_preview_total: z.number().int().nonnegative().default(0),
})

const fsnReviewSummarySchema = z.object({
  total: z.number().int().nonnegative(),
  published: z.number().int().nonnegative(),
  pending: z.number().int().nonnegative().default(0),
  hidden: z.number().int().nonnegative().default(0),
  rejected: z.number().int().nonnegative().default(0),
})

/** `FsnImportSourceReader::customPagesWithContent()`: one of the florist's live FSN pages whose content the import leaves behind. */
export const fsnImportCustomPageWithContentSchema = z.object({
  page_id: z.number().int().positive(),
  title: z.string(),
  path: z.string(),
  text_characters: z.number().int().nonnegative(),
  images: z.number().int().nonnegative(),
  embeds: z.array(z.string()),
  products: z.number().int().nonnegative(),
})

export const fsnImportAccountSchema = z.object({
  username: z.string(),
  sitename: z.string().nullable(),
  enabled: z.boolean(),
  hosted_url: z.string().nullable(),
  active_product_id: z.number().int().nullable(),
  template_id: z.number().int().nullable(),
  country: z.string().nullable(),
  has_active_website_product: z.boolean(),
  logo_url: z.string().nullable(),
})

export const fsnImportPlanSchema = z.object({
  status: z.enum(['ready', 'account_required']),
  organization_id: z.number().int().positive(),
  fsn_client_id: z.number().int().positive(),
  account: fsnImportAccountSchema.nullable(),
  accounts: z.array(fsnImportAccountSchema),
  available_sections: z.array(fsnImportSectionSchema).default(allFsnImportSections),
  selected_sections: z.array(fsnImportSectionSchema),
  product_options: fsnImportProductOptionsSchema,
  website_navigation: fsnImportWebsiteNavigationSchema,
  sections: z
    .object({
      products: z
        .object({
          selected: z.boolean(),
          summary: z.object({
            fsn_current_page_products: z.number().int().nonnegative(),
            fsn_off_page_products: z.number().int().nonnegative().default(0),
            custom_current_page_products: z.number().int().nonnegative(),
            custom_off_page_products: z.number().int().nonnegative(),
            ready: z.number().int().nonnegative(),
            skipped: z.number().int().nonnegative(),
            collections_to_create: z.number().int().nonnegative(),
            collections_to_update: z.number().int().nonnegative(),
          }),
          preview_rows: z.array(
            z.object({
              source_key: z.string(),
              name: z.string(),
              product_source: z.string(),
              collection_source: z.string(),
              has_active_page_placement: z.boolean(),
              collections: z.array(z.string()),
            })
          ),
          custom_pages_with_content: z.array(fsnImportCustomPageWithContentSchema).default([]),
        })
        .optional(),
      notification_contacts: z
        .object({
          selected: z.boolean(),
          summary: z.object({
            total: z.number().int().nonnegative(),
            emails: z.number().int().nonnegative(),
            phones: z.number().int().nonnegative(),
            texts: z.number().int().nonnegative(),
            faxes: z.number().int().nonnegative(),
            enabled: z.number().int().nonnegative().default(0),
            disabled: z.number().int().nonnegative().default(0),
          }),
          preview_rows: z.array(fsnNotificationContactPreviewRowSchema).default([]),
        })
        .optional(),
      location: z
        .object({
          selected: z.boolean(),
          summary: z.object({
            available: z.boolean(),
            pickup_enabled: z.boolean().nullable(),
            delivery_enabled: z.boolean().nullable(),
            pickup_prep_hours: z.number().int().nullable().optional(),
            delivery_cutoff_time: z.string().nullable().optional(),
          }),
          preview_rows: z.array(fsnGenericPreviewRowSchema).default([]),
        })
        .optional(),
      delivery_areas: z
        .object({
          selected: z.boolean(),
          summary: z.object({
            cities: z.number().int().nonnegative(),
            postal_codes: z.number().int().nonnegative(),
            distance_ranges: z.number().int().nonnegative(),
          }),
          preview_rows: fsnDeliveryAreaPreviewSchema.shape.preview_rows,
          distance_preview_rows: fsnDeliveryAreaPreviewSchema.shape.distance_preview_rows,
          distance_preview_total: fsnDeliveryAreaPreviewSchema.shape.distance_preview_total,
        })
        .optional(),
      customers: z
        .object({
          selected: z.boolean(),
          summary: z.object({
            total: z.number().int().nonnegative(),
          }),
          preview_rows: z.array(fsnGenericPreviewRowSchema).default([]),
        })
        .optional(),
      reviews: z
        .object({
          selected: z.boolean(),
          summary: fsnReviewSummarySchema,
          preview_rows: z.array(fsnGenericPreviewRowSchema).default([]),
        })
        .optional(),
      add_ons: z
        .object({
          selected: z.boolean(),
          summary: z.object({
            total: z.number().int().nonnegative(),
          }),
          preview_rows: z.array(fsnGenericPreviewRowSchema).default([]),
        })
        .optional(),
      social_links: z
        .object({
          selected: z.boolean(),
          summary: z.object({
            total: z.number().int().nonnegative(),
            platforms: z.array(z.string()),
          }),
          preview_rows: z.array(fsnSocialLinkPreviewRowSchema).default([]),
        })
        .optional(),
      logo: z
        .object({
          selected: z.boolean(),
          summary: z.object({
            available: z.boolean(),
          }),
        })
        .optional(),
    })
    .partial(),
  coming_soon_sections: z.array(
    z.object({
      key: z.string(),
      label: z.string(),
    })
  ),
  summary: z.object({
    products_ready: z.number().int().nonnegative(),
    location_ready: z.boolean().default(false),
    delivery_areas_ready: z.number().int().nonnegative().default(0),
    customers_ready: z.number().int().nonnegative().default(0),
    reviews_ready: z.number().int().nonnegative().default(0),
    add_ons_ready: z.number().int().nonnegative().default(0),
    contacts_ready: z.number().int().nonnegative(),
    social_links_ready: z.number().int().nonnegative(),
    logo_available: z.boolean(),
  }),
  fingerprint: z.string().nullable(),
})

export const fsnImportEstimateSchema = z.object({
  status: z.enum(['ready', 'account_required']),
  organization_id: z.number().int().positive(),
  fsn_client_id: z.number().int().positive(),
  account: fsnImportAccountSchema.nullable(),
  accounts: z.array(fsnImportAccountSchema),
  available_sections: z.array(fsnImportSectionSchema).default(allFsnImportSections),
  products: z.object({
    fsn: z.object({
      current_pages: z.number().int().nonnegative(),
      all_active: z.number().int().nonnegative(),
      off_page: z.number().int().nonnegative(),
    }),
    custom: z.object({
      current_pages: z.number().int().nonnegative(),
      all: z.number().int().nonnegative(),
      off_page: z.number().int().nonnegative(),
    }),
    current_page_total: z.number().int().nonnegative(),
  }),
  notification_contacts: z.object({
    total: z.number().int().nonnegative(),
    emails: z.number().int().nonnegative(),
    phones: z.number().int().nonnegative(),
    texts: z.number().int().nonnegative(),
    faxes: z.number().int().nonnegative(),
    enabled: z.number().int().nonnegative().default(0),
    disabled: z.number().int().nonnegative().default(0),
    preview_rows: z.array(fsnNotificationContactPreviewRowSchema).default([]),
  }),
  location: z
    .object({
      available: z.boolean(),
      pickup_enabled: z.boolean().nullable(),
      delivery_enabled: z.boolean().nullable(),
      preview_rows: z.array(fsnGenericPreviewRowSchema).default([]),
    })
    .default({
      available: false,
      pickup_enabled: null,
      delivery_enabled: null,
      preview_rows: [],
    }),
  delivery_areas: z
    .object({
      cities: z.number().int().nonnegative(),
      postal_codes: z.number().int().nonnegative(),
      distance_ranges: z.number().int().nonnegative(),
      preview_rows: fsnDeliveryAreaPreviewSchema.shape.preview_rows,
      distance_preview_rows: fsnDeliveryAreaPreviewSchema.shape.distance_preview_rows,
      distance_preview_total: fsnDeliveryAreaPreviewSchema.shape.distance_preview_total,
    })
    .default({
      cities: 0,
      postal_codes: 0,
      distance_ranges: 0,
      preview_rows: [],
      distance_preview_rows: [],
      distance_preview_total: 0,
    }),
  customers: z
    .object({
      total: z.number().int().nonnegative(),
      preview_rows: z.array(fsnGenericPreviewRowSchema).default([]),
    })
    .default({
      total: 0,
      preview_rows: [],
    }),
  reviews: z
    .object({
      ...fsnReviewSummarySchema.shape,
      preview_rows: z.array(fsnGenericPreviewRowSchema).default([]),
    })
    .default({
      total: 0,
      published: 0,
      pending: 0,
      hidden: 0,
      rejected: 0,
      preview_rows: [],
    }),
  add_ons: z
    .object({
      total: z.number().int().nonnegative(),
      preview_rows: z.array(fsnGenericPreviewRowSchema).default([]),
    })
    .default({
      total: 0,
      preview_rows: [],
    }),
  social_links: z.object({
    total: z.number().int().nonnegative(),
    platforms: z.array(z.string()),
    preview_rows: z.array(fsnSocialLinkPreviewRowSchema).default([]),
  }),
  logo: z.object({
    available: z.boolean(),
    url: z.string().nullable(),
  }),
})

export const fsnImportAttemptProgressSchema = z.object({
  processed: z.number().int().nonnegative(),
  total: z.number().int().nonnegative(),
  percent: z.number().int().min(0).max(100),
})

export const fsnImportAttemptStageSchema = z.object({
  key: z.enum([
    'resolving_account',
    'reading_fsn_data',
    'preparing_images',
    'importing_products',
    'updating_location',
    'importing_delivery_areas',
    'importing_customers',
    'importing_reviews',
    'importing_add_ons',
    'importing_contacts',
    'updating_design',
    'finalizing',
  ]),
  label: z.string(),
  status: z.enum(['pending', 'current', 'completed', 'failed']),
  processed: z.number().int().nonnegative(),
  total: z.number().int().nonnegative(),
  percent: z.number().int().min(0).max(100),
})

export const fsnImportAttemptSchema = z.object({
  id: z.uuid(),
  status: z.enum(['queued', 'running', 'completed', 'failed']),
  stage: z.enum([
    'queued',
    'resolving_account',
    'reading_fsn_data',
    'preparing_images',
    'importing_products',
    'updating_location',
    'importing_delivery_areas',
    'importing_customers',
    'importing_reviews',
    'importing_add_ons',
    'importing_contacts',
    'updating_design',
    'finalizing',
    'completed',
    'failed',
  ]),
  stage_label: z.string(),
  message: z.string().nullable(),
  fsn_client_id: z.number().int().positive(),
  fsn_username: z.string().nullable(),
  selected_sections: z.array(fsnImportSectionSchema),
  product_options: fsnImportProductOptionsSchema,
  website_navigation: fsnImportWebsiteNavigationSchema,
  progress: fsnImportAttemptProgressSchema,
  stages: z.array(fsnImportAttemptStageSchema).default([]),
  plan_summary: z
    .object({
      products_ready: z.number().int().nonnegative(),
      location_ready: z.boolean().default(false),
      delivery_areas_ready: z.number().int().nonnegative().default(0),
      customers_ready: z.number().int().nonnegative().default(0),
      reviews_ready: z.number().int().nonnegative().default(0),
      add_ons_ready: z.number().int().nonnegative().default(0),
      contacts_ready: z.number().int().nonnegative(),
      social_links_ready: z.number().int().nonnegative(),
      logo_available: z.boolean(),
    })
    .nullable()
    .optional(),
  result: z.unknown().nullable(),
  error: z
    .object({
      message: z.string(),
    })
    .nullable(),
  queued_at: z.string().nullable(),
  started_at: z.string().nullable(),
  finished_at: z.string().nullable(),
})

export const fsnImportActiveAttemptResponseSchema = z.object({
  attempt: fsnImportAttemptSchema.nullable(),
})
